import './OneTimeNotificationModal.css';
import React, { useState, useEffect } from 'react';
import { Form, Flex, Divider, message, Dropdown, Button } from 'antd';
import type { FormInstance } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { CustomRadioGroup } from '../../../components';
import {
  NotifyViaPills,
  AlertRecipientSelector,
  AlertFooter,
  OneTimeMessageBox,
} from './components';
import SchedulePicker from '../ShipmentToolbar/SchedulePicker';
import { getFormattedUserTimeZone } from '../../../common/utils/utils';
import {
  createOneTimeShipmentNotification,
  OneTimeNotificationPayload,
  NotificationBulkScope,
  getOneTimeMessageHistory,
  MessageHistoryItem,
} from '@/apis/shipment';
//import { CreateSendToArray } from '@/common/utils/utils';
import { ErrorLike, SingleShipmentDataExtended } from '@/apis/types';
import useFetchSingleOneTime from '@/hooks/useFetchSingleOneTime';
import RecipientValidationField from './components/RecipientValidationField';
import { useSpin } from '@/components/SpinContext';
import { useGateValue } from '@statsig/react-bindings';
import OneTimeCallChoices from './components/OneTimeCallChoices';

// Component types

export interface NotificationData {
  additional_recipients?: string[]; // or string if it's a single email or comma-separated
  alert_type?: string;
  alert_types?: string[];
  change_by?: string;
  day?: string; // e.g. 'Monday', or could be a number if it's day index
  frequency?: string; // e.g. 'daily', 'weekly', etc.
  id?: string | null;
  is_at_risk?: boolean;
  is_critical?: boolean;
  is_delivery_confirmation?: boolean;
  is_delivery_exception?: boolean;
  is_pickup_confirmation?: boolean;
  is_sent?: boolean;
  location?: string;
  notification_method?: string; // e.g. 'email', 'sms', etc.
  scheduled_at?: string | Date;
  send_to?: {
    teams_users: TeamUser[];
    additional: { key: string; enabled?: boolean }[];
  }; // can replace with more specific type if known
  sent_at?: string | Date;
  teams?: unknown[]; // replace with specific team type if available
  time_1?: Dayjs | null;
  time_2?: Dayjs | null;
  users?: unknown[]; // replace with specific user type if available
  notification_mothod?: NotificationMethod;
  message?: { text?: string };
}
interface OneTimeNotificationModalProps {
  setOpenNotificationModal: (open: boolean) => void;
  onSuccess: () => void;
  unauthorize: (error?: ErrorLike) => void;
  shipment?: SingleShipmentDataExtended;
  recipientData?: unknown[];
  loggedInUserId?: string;
  checkTokenExpiry: () => string;
  isMultiple?: boolean;
  shipmentId: string | string[];
  notificationId?: string;
  onNotificationCreated?: () => void;
  bulkScope?: NotificationBulkScope | null;
}

interface FormValues {
  call_contact_id?: string;
  call_agent_id?: string;
  message?: string;
  users?: string[];
  notification_method?: string;
}

interface Team {
  id: string;
  select_all: boolean;
  permissions?: {
    create_self?: boolean;
  };
  [key: string]: unknown;
}

// interface AdditionalItem {
//   key: string;
//   enabled?: boolean;
// }

interface User {
  id: string; // assuming IDs could be either numeric or string
  name?: string; // optional, if users have names
  email?: string; // optional, if available
  [key: string]: unknown; // to allow any extra fields from API
}

interface TeamUser {
  id?: string | number;
  select_all?: boolean;
  create_self?: boolean;
  users?: (User | string)[];
}

interface Team {
  users?: (User | string)[];
}

type AiNotificationMethod = 'AI_EMAIL' | 'AI_CALL';
type NotificationMethod = AiNotificationMethod | '';

/**
 * Modal component for creating one-time shipment notifications
 *
 * @param setOpenNotificationModal - Function to control modal visibility
 * @param onSuccess - Callback executed on successful notification creation
 * @param unauthorize - Error handler for unauthorized/failed requests
 * @param shipment - Optional shipment data
 * @param recipientData - List of available recipients
 * @param loggedInUserId - ID of the currently logged-in user
 * @param checkTokenExpiry - Function to retrieve authentication token
 * @param isMultiple - Whether notification is for multiple shipments
 * @param shipmentId - ID(s) of shipment(s) to notify about
 */
const OneTimeNotificationModal: React.FC<OneTimeNotificationModalProps> = ({
  setOpenNotificationModal,
  onSuccess,
  unauthorize,
  shipment,
  recipientData,
  loggedInUserId,
  checkTokenExpiry,
  isMultiple = false,
  onNotificationCreated,
  shipmentId,
  notificationId,
  bulkScope = null,
}) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  // the hook types its payload as unknown; this is the one-time notification record
  const oneTimeData = useFetchSingleOneTime(notificationId ?? '')
    .oneTimeData as NotificationData | null;
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null);
  const [selectedRadioOption, setSelectedRadioOption] = useState<
    'send_now' | 'schedule'
  >('send_now');
  const [form]: [FormInstance] = Form.useForm();
  const savedContactId = Form.useWatch('call_contact_id', form) as
    string | undefined;
  const callChoicesGate = useGateValue('shipment_one_time_call_choices');
  const { showSpin, hideSpin } = useSpin();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const [teams, setTeams] = useState<Team[]>([]);
  const [notificationMethod, setNotificationMethod] = useState('');
  const showCallChoices =
    callChoicesGate &&
    !isMultiple &&
    !notificationId &&
    !oneTimeData?.id &&
    selectedRadioOption === 'send_now' &&
    notificationMethod === 'AI_CALL';
  const [isRecipientsDropdownOpen, setIsRecipientsDropdownOpen] =
    useState<boolean>(false);
  const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
  const [additional, setAdditional] = useState<string[]>([]);
  const [recipientsValues, setRecipientsValues] = useState<
    OneTimeNotificationPayload['send_to']
  >([]);
  const [messageHistory, setMessageHistory] = useState<MessageHistoryItem[]>(
    []
  );
  const handleTeamsData = (data: Team[]): void => {
    setTeams(data);
  };

  useEffect(() => {
    const token = checkTokenExpiry();
    getOneTimeMessageHistory(token)
      .then((res) => {
        const data = res?.data?.data?.data;
        if (data && Array.isArray(data)) {
          setMessageHistory(data);
        }
      })
      .catch((error) => {
        console.error('Error fetching message history:', error);
      });
  }, [checkTokenExpiry]);

  useEffect(() => {
    if (oneTimeData) {
      form.setFieldsValue({
        additional_recipients: oneTimeData?.additional_recipients,
        id: oneTimeData?.id,
        notification_method: oneTimeData?.notification_method,
        send_to: oneTimeData?.send_to,
        teams: oneTimeData?.teams,
        time_1: oneTimeData?.time_1
          ? dayjs(oneTimeData.time_1, 'h:mm a')
          : null,
        time_2: oneTimeData?.time_2
          ? dayjs(oneTimeData.time_2, 'h:mm a')
          : null,
        users: oneTimeData?.users,
        message:
          typeof oneTimeData?.message === 'object'
            ? oneTimeData?.message?.text || ''
            : typeof oneTimeData?.message === 'string'
              ? oneTimeData.message
              : '',
      });
      if (oneTimeData?.scheduled_at) {
        setSelectedRadioOption('schedule');
        const parsed = dayjs(oneTimeData.scheduled_at, 'YYYY-MM-DD HH:mm:ss');
        setSelectedDate(parsed);
        setSelectedTime(parsed);
      }

      setNotificationMethod(oneTimeData?.notification_method ?? '');
      const recepientsArr: string[] = [];
      const formValues: OneTimeNotificationPayload['send_to'] = [];
      const teamsUsers: TeamUser[] = Array.isArray(
        oneTimeData?.send_to?.teams_users
      )
        ? oneTimeData?.send_to?.teams_users
        : [];
      teamsUsers.forEach((element) => {
        if (element.id == null) return;
        const obj: OneTimeNotificationPayload['send_to'][number] = {
          id: String(element.id),
          select_all: element.select_all === true,
          create_self: element.create_self === true,
          users: [],
        };
        if (obj) {
          const teamData = teams.find((t) => t.id === element?.id);
          if (element.select_all) {
            if (teamData) {
              teamData.select_all = true;
            }
            teamData?.users?.forEach((user) => {
              if (typeof user === 'object') user.is_checked = true;
            });
            const id = `${element.id}::ALL_MEMBERS`;
            recepientsArr.push(id);
            obj.users = [];
          } else {
            if (element?.users?.length) {
              const userIds: string[] = [];
              element.users.forEach((user) => {
                const userId = typeof user === 'string' ? user : user.id;
                const id = `${element.id}::${userId}`;
                teamData?.users?.forEach((u) => {
                  if (typeof u === 'object' && u.id === userId) {
                    u.is_checked = true;
                  }
                });
                userIds.push(userId);
                recepientsArr.push(id);
              });
              obj.users = userIds;
            }
          }
          formValues.push(obj);
        }
      });
      const additionalKeys = (
        Array.isArray(oneTimeData?.send_to?.additional)
          ? oneTimeData.send_to.additional
          : []
      )
        .filter((a) => a.enabled !== false)
        .map((a) => a.key);

      const values = [...recepientsArr, ...additionalKeys];
      setSelectedRecipients(values);
      setAdditional(additionalKeys);
      setRecipientsValues(formValues);
    }
  }, [oneTimeData, form, teams]);

  const handleSubmit = (values: FormValues): void => {
    // const { additionalRecipientValues, sendToArray } = CreateSendToArray(
    //   values.users,
    //   teams
    // );
    setIsFormValid(true);
    showSpin();
    const userTz = getFormattedUserTimeZone().replace(
      /^Timezone set to\s*/i,
      ''
    );
    const sendTo = recipientsValues;
    if (
      selectedRadioOption === 'schedule' &&
      (!selectedDate || !selectedTime)
    ) {
      message.error('Please select both date and time to schedule.');
      return;
    }
    const oneTimePayload: OneTimeNotificationPayload = {
      message: {
        body_html: values.message?.replace(/\n/g, '<br/>') || '',
        body_text: values.message || '',
      },
      send_now: selectedRadioOption === 'send_now',
      notification_method: form.getFieldValue('notification_method'),
      timezone: userTz,
      additional_recipients:
        showCallChoices && values.call_contact_id ? [] : additional,
      scheduled_at:
        selectedRadioOption === 'schedule'
          ? dayjs
              .tz(
                `${selectedDate?.format('YYYY-MM-DD')} ${selectedTime?.format('HH:mm')}`,
                userTz
              )
              .utc()
              .toISOString()
          : null,

      send_to: showCallChoices && values.call_contact_id ? [] : sendTo,
      ...(showCallChoices && {
        call_contact_id: values.call_contact_id,
        call_agent_id: values.call_agent_id,
      }),
      id: oneTimeData?.id ? oneTimeData.id : null,
      ...(bulkScope ?? {
        shipment_ids: isMultiple ? shipmentId : [shipmentId],
        bulk: isMultiple,
      }),
    };

    const token = checkTokenExpiry();
    createOneTimeShipmentNotification(
      token,
      oneTimePayload,
      oneTimeData?.id ? oneTimeData?.id : null
    )
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          form.resetFields();
          void message.success(data?.message || 'Notification saved');
          if (!notificationId && onNotificationCreated) {
            onNotificationCreated();
          }
          onSuccess();
        } else {
          void message.error(data?.message || 'Failed to save notification');
        }
      })
      .catch((error) => {
        unauthorize(error);
      })

      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  const handleResetOnNotificationMethodChange = (val: string) => {
    if (val) {
      // form.setFieldsValue({ users: null });
      setSelectedRecipients([]);
      setRecipientsValues([]);
      setAdditional([]);
      if (teams.length > 0) {
        teams.forEach((element) => {
          element.select_all = false;
          element?.users?.forEach((user) => {
            if (typeof user === 'object') user.is_checked = false;
          });
        });
      }
    }
  };

  return (
    <>
      <Form form={form} onFinish={handleSubmit} layout='vertical'>
        <Flex vertical gap={16} style={{ margin: '20px 0px' }}>
          <>
            <div>
              <CustomRadioGroup
                value={selectedRadioOption}
                horizontal={false}
                gap='190px'
                onChange={(e) => setSelectedRadioOption(e.target.value)}
                options={[
                  { label: 'Send now', value: 'send_now' },
                  { label: 'Schedule', value: 'schedule' },
                ]}
              />
            </div>
            {selectedRadioOption === 'schedule' && (
              <SchedulePicker
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                selectedTime={selectedTime}
                setSelectedTime={setSelectedTime}
              />
            )}
            <Divider className='notification-divider' />
            <NotifyViaPills
              form={form}
              notificationType='ai'
              disabledMethods={
                isMultiple || shipment?.can_edit_ai_communications === true
                  ? []
                  : ['AI_CALL', 'AI_EMAIL']
              }
              setNotificationMethod={(method: string) =>
                setNotificationMethod(method as NotificationMethod)
              }
              resetOnChange={handleResetOnNotificationMethodChange}
            />
            <OneTimeMessageBox
              shipment={shipment}
              notifyVia={notificationMethod || undefined}
            />
            {messageHistory.length > 0 && (
              <div className='one-time-message-history'>
                <Dropdown
                  menu={{
                    items: messageHistory.map((item, index) => ({
                      key: index.toString(),
                      label: `${item.original_message.substring(0, 60)}${item.original_message.length > 60 ? '...' : ''}`,
                      onClick: () => {
                        form.setFieldsValue({ message: item.original_message });
                      },
                    })),
                    style: {
                      maxHeight: '200px',
                      overflowY: 'auto',
                    },
                  }}
                  trigger={['click']}
                  placement='bottomRight'
                  getPopupContainer={(trigger) =>
                    trigger.parentElement || document.body
                  }
                  autoAdjustOverflow={false}
                >
                  <Button
                    type='text'
                    className='message-history-dropdown'
                    style={{
                      padding: 0,
                      height: 'auto',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: 'var(--text-primary)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    Pre-fill past message{' '}
                    <span className='one-time-message-history-arrow'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='8'
                        height='5'
                        viewBox='0 0 8 5'
                        fill='none'
                      >
                        <path
                          d='M7.06 0L4 3.05333L0.94 0L0 0.94L4 4.94L8 0.94L7.06 0Z'
                          fill='black'
                        />
                      </svg>
                    </span>
                  </Button>
                </Dropdown>
              </div>
            )}
            {showCallChoices && (
              <OneTimeCallChoices checkTokenExpiry={checkTokenExpiry} />
            )}
            {!(showCallChoices && savedContactId) && (
              <RecipientValidationField
                form={form}
                selectedRecipients={selectedRecipients}
              >
                <AlertRecipientSelector
                  alertType={'ONE_TIME'}
                  isRecipientsDropdownOpen={isRecipientsDropdownOpen}
                  loggedInUserId={loggedInUserId}
                  form={form}
                  recipientData={recipientData}
                  setIsRecipientsDropdownOpen={setIsRecipientsDropdownOpen}
                  sendTeamsToParent={handleTeamsData}
                  notificationMethod={notificationMethod}
                  selectedRecipients={selectedRecipients}
                  setSelectedRecipients={setSelectedRecipients}
                  setRecipientsValues={setRecipientsValues}
                  setAdditional={setAdditional}
                  recipientsValues={recipientsValues}
                />
              </RecipientValidationField>
            )}
            {notificationMethod === 'AI_EMAIL' ||
            notificationMethod === 'AI_CALL' ? (
              <p className='zl-para-400-12 color-5e'>
                {`If recipient isn’t reached, seems confused, or sentiment is
                negative, AI will flag action as "Needs attention"`}
                .
              </p>
            ) : null}
          </>
        </Flex>
        <AlertFooter
          text={'Send'}
          setStep={() => {}}
          setOpenNotificationModal={setOpenNotificationModal}
          isFormValid={isFormValid}
        />
      </Form>
    </>
  );
};
export default OneTimeNotificationModal;
